import React, { useContext } from 'react';
import {
  Box,
  Text,
  Image,
  VStack,
  HStack,
  Badge,
  Progress,
  Avatar,
  Flex,
  Link,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  useColorMode,
  Tooltip,
  Icon,
} from '@chakra-ui/react';
import { useHistory } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { FaBookOpen, FaEye } from 'react-icons/fa';
import { MdOndemandVideo } from 'react-icons/md';
import { AiOutlineFilePpt } from 'react-icons/ai';
import { BsQuestionSquare } from 'react-icons/bs';
import { VscBook } from 'react-icons/vsc';
import { FaClipboardList } from 'react-icons/fa';
import { ExternalLinkIcon } from '@chakra-ui/icons';
import AuthContext from '../../../contexts/AuthContext';
import courseProgressQuery from '../../../graphql/queries/courseProgress';
import { useTranslation } from 'react-i18next';

const contentTypeIcons = {
  video: MdOndemandVideo,
  document: AiOutlineFilePpt,
  quiz: BsQuestionSquare,
  survey: FaClipboardList,
  link: ExternalLinkIcon,
  homework: VscBook,
};

const CourseProgressBar = ({ courseId }) => {
  const { user } = useContext(AuthContext);
  const { t } = useTranslation();

  const { data } = useQuery(courseProgressQuery, {
    variables: { courseId, studentId: user?._id },
    skip: !user?._id,
    fetchPolicy: 'network-only',
  });

  const progress = data?.courseProgress;

  if (!user || !progress) return null;

  return (
    <Box w='full' mt={2}>
      <Flex justifyContent='space-between' mb={1}>
        <Text fontSize='xs' color='gray.500'>
          {t('course.progress')}
        </Text>
        <Text fontSize='xs' color='gray.500'>
          {progress.completedContents}/{progress.totalContents} ({progress.progressPercent}%)
        </Text>
      </Flex>
      <Progress value={progress.progressPercent} colorScheme='green' borderRadius='full' size='sm' hasStripe />
    </Box>
  );
};

const CourseCard = ({ course }) => {
  const history = useHistory();
  const { colorMode } = useColorMode();
  const { t } = useTranslation();

  const courseItems = [...(course?.courseItems || [])].sort((a, b) => (a.index || 0) - (b.index || 0));
  const totalSubjects = courseItems.length;

  return (
    <Box
      borderWidth='2px'
      borderRadius='xl'
      overflow='hidden'
      boxShadow='lg'
      bg={colorMode === 'light' ? 'white' : 'gray.700'}
      _hover={{ boxShadow: '2xl', transform: 'translateY(-4px)' }}
      transition='all 0.3s'
      h='100%'
      display='flex'
      flexDirection='column'
    >
      {/* Course Header Image */}
      <Box position='relative' h='180px' w='full'>
        <Image
          src={
            course?.photo ||
            'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
          }
          objectFit='cover'
          w='100%'
          h='100%'
        />
        <Box
          position='absolute'
          top='0'
          left='0'
          w='100%'
          h='100%'
          bg='rgba(0, 0, 0, 0.45)'
          display='flex'
          flexDirection='column'
          alignItems='center'
          justifyContent='center'
          p={4}
        >
          <Text fontSize='xl' fontWeight='bold' color='white' textAlign='center' noOfLines={2}>
            {course?.name}
          </Text>
          <HStack mt={2}>
            <Badge colorScheme='teal' variant='solid' fontSize='xs'>
              <HStack spacing={1}>
                <FaBookOpen />
                <Text>
                  {totalSubjects} {t('course.subject')}
                </Text>
              </HStack>
            </Badge>
          </HStack>
        </Box>
      </Box>

      {/* Course Description */}
      <Box p={4} flex={1} display='flex' flexDirection='column'>
        {course?.description && (
          <Text fontSize='sm' color='gray.600' mb={3} noOfLines={2}>
            {course.description}
          </Text>
        )}

        {/* Course Progress */}
        <CourseProgressBar courseId={course._id} />

        {/* Subjects Accordion */}
        <Accordion allowMultiple mt={3} flex={1}>
          {courseItems.map((item, idx) => (
            <AccordionItem key={item._id} border='none' mb={1}>
              <AccordionButton
                borderRadius='md'
                _hover={{ bg: colorMode === 'light' ? 'gray.100' : 'gray.600' }}
                px={2}
                py={2}
              >
                <HStack flex={1} spacing={2}>
                  <Badge colorScheme='purple' borderRadius='full' px={2} fontSize='xs'>
                    {idx + 1}
                  </Badge>
                  {item.subject?.photo && <Avatar src={item.subject.photo} size='xs' />}
                  <Text fontSize='sm' fontWeight='semibold' textAlign='left' noOfLines={1} flex={1}>
                    {item.subject?.name || 'Unknown'}
                  </Text>
                </HStack>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel pb={2} px={2}>
                <Link onClick={() => history.push(`/subject/${item.subject?._id}`)} _hover={{ textDecoration: 'none' }}>
                  <Text fontSize='xs' color='blue.500' mb={2} cursor='pointer' _hover={{ textDecoration: 'underline' }}>
                    {t('course.enterSubject')} →
                  </Text>
                </Link>
                {item.subject?.teacher && (
                  <HStack mb={2} spacing={1}>
                    <Avatar src={item.subject.teacher.avatar} size='2xs' />
                    <Text fontSize='xs' color='gray.500'>
                      {item.subject.teacher.firstname} {item.subject.teacher.lastname}
                    </Text>
                  </HStack>
                )}
                {item.subject?.contents?.length > 0 ? (
                  <VStack align='stretch' spacing={1}>
                    {item.subject.contents.slice(0, 5).map((content) => (
                      <HStack
                        key={content._id}
                        fontSize='xs'
                        color='gray.600'
                        justify='space-between'
                        px={2}
                        py={1}
                        borderRadius='sm'
                        bg={colorMode === 'light' ? 'gray.50' : 'gray.600'}
                      >
                        <HStack spacing={1} flex={1}>
                          <Icon as={contentTypeIcons[content.type] || FaBookOpen} boxSize={3} />
                          <Text noOfLines={1}>{content.name}</Text>
                          {content.contentGroup?.name && (
                            <Badge colorScheme='purple' fontSize='2xs' variant='subtle'>
                              {content.contentGroup.name}
                            </Badge>
                          )}
                          {content.public && (
                            <Badge colorScheme='green' fontSize='2xs' variant='subtle'>
                              {t('course.free')}
                            </Badge>
                          )}
                        </HStack>
                        {content.viewCount > 0 && (
                          <Tooltip label={t('course.viewCount')}>
                            <HStack spacing={1} color='gray.400'>
                              <FaEye size={10} />
                              <Text fontSize='2xs'>{content.viewCount}</Text>
                            </HStack>
                          </Tooltip>
                        )}
                      </HStack>
                    ))}
                    {item.subject.contents.length > 5 && (
                      <Text fontSize='xs' color='gray.400' textAlign='center' mt={1}>
                        +{item.subject.contents.length - 5} {t('course.moreContent')}
                      </Text>
                    )}
                  </VStack>
                ) : (
                  <Text fontSize='xs' color='gray.400'>
                    {t('course.noContent')}
                  </Text>
                )}
              </AccordionPanel>
            </AccordionItem>
          ))}
        </Accordion>
      </Box>
    </Box>
  );
};

export default CourseCard;
